import React, { useState } from 'react';
import { X, Mail } from 'lucide-react';
import { useTheme } from '../context/ThemeContext.tsx';
import { API_URL, API_BASE_URL } from '../config/api.ts';

interface LoginModalProps {
    isOpen: boolean;
    onClose: () => void;
    onLoginSuccess?: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onLoginSuccess }) => {
    const { isDarkMode } = useTheme();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLogin, setIsLogin] = useState(true);
    const [confirmAge, setConfirmAge] = useState(false);
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showEmailLogin, setShowEmailLogin] = useState(false);
    const [username, setUsername] = useState('');

    if (!isOpen) return null;

    const handleGoogleLogin = () => {
        window.location.href = `${API_BASE_URL}/api/auth/google`; // We use BASE here because it's a redirect, and /api/auth is the structure
    };

    const handleDiscordLogin = () => {
        window.location.href = `${API_BASE_URL}/api/auth/discord`;
    };

    const handleSubscribeStarLogin = () => {
        window.location.href = `${API_BASE_URL}/api/auth/subscribestar`;
    };

    const handleEmailLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email || !password) {
            setError('Please fill all fields');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await fetch(`${API_URL}/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
                credentials: 'include'
            });

            const data = await response.json();

            if (data.success) {
                onLoginSuccess?.();
                onClose();
            } else {
                setError(data.message || 'Login failed');
            }
        } catch (err) {
            setError('Connection error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email || !password || !username) {
            setError('Please fill all fields');
            return;
        }

        if (!confirmAge) {
            setError('You must confirm you are 18 or older');
            return;
        }

        if (!agreeTerms) {
            setError('You must agree to the Terms of Service');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await fetch(`${API_URL}/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password, username }),
                credentials: 'include'
            });

            const data = await response.json();

            if (data.success) {
                onLoginSuccess?.();
                onClose();
            } else {
                setError(data.message || 'Registration failed');
            }
        } catch (err) {
            setError('Connection error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const SocialButton = ({
        icon,
        label,
        onClick,
        color = '#333'
    }: {
        icon: React.ReactNode;
        label: string;
        onClick: () => void;
        color?: string;
    }) => (
        <button
            onClick={onClick}
            style={{
                width: '100%',
                padding: '12px 20px',
                borderRadius: '12px',
                border: '1px solid rgba(168, 85, 247, 0.2)',
                background: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: '500',
                color: color,
                transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(168, 85, 247, 0.05)';
                e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.4)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.borderColor = 'rgba(168, 85, 247, 0.2)';
            }}
        >
            {icon}
            <span>{label}</span>
        </button>
    );

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(8px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000,
                padding: '20px'
            }}
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: 'linear-gradient(180deg, #faf5ff 0%, #fce7f3 100%)',
                    borderRadius: '24px',
                    padding: '32px',
                    width: '100%',
                    maxWidth: '400px',
                    position: 'relative',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                }}
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        border: '1px solid rgba(168, 85, 247, 0.3)',
                        background: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: '#a855f7'
                    }}
                >
                    <X size={16} />
                </button>

                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <h2 style={{
                        fontSize: '24px',
                        fontWeight: '700',
                        background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        marginBottom: '8px'
                    }}>
                        Welcome to EcstasyAI
                    </h2>
                    <p style={{
                        fontSize: '14px',
                        color: '#666',
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                    }}>
                        {isLogin ? 'SIGN IN WITH' : 'SIGN UP WITH'}
                    </p>
                </div>

                {/* Social Buttons */}
                {!showEmailLogin && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
                        <SocialButton
                            icon={
                                <svg width="20" height="20" viewBox="0 0 24 24">
                                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                </svg>
                            }
                            label="Google"
                            onClick={handleGoogleLogin}
                        />

                        <SocialButton
                            icon={
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="#5865F2">
                                    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
                                </svg>
                            }
                            label="Discord"
                            onClick={handleDiscordLogin}
                            color="#5865F2"
                        />

                        <SocialButton
                            icon={
                                <svg width="20" height="20" viewBox="0 0 936.86 936.86" fill="#ff6b6b">
                                    <path d="M479.11 936.74c-3.55.08-7.11.12-10.68.12C209.72 936.86 0 727.14 0 468.43 0 280.08 111.16 117.7 271.45 43.31c21.94 40.07 49.93 72.72 91.53 110.12 21.25 19.1 47.21 38.95 75.34 62.67 45.67 38.51 71.7 69.35 86.24 95.59-17.1-3.2-35.76-4.93-56.13-4.93-27.71 0-52.3 3.11-74.11 8.69v-26.46H237.43v91.11c0 9.44-1.21 41.78 6.45 80.5-5.31 15.91-9.07 31.74-11.7 46.78-4.12 23.57-5.42 45.18-5.42 62.05 0 79.11 22.23 164.89 123.56 253.78 101.17 88.75 107.98 87.3 128.79 113.53z" />
                                    <path d="M936.86 468.43c0 188.38-111.2 350.79-271.54 425.15-.53-1.03-35.3-66.92-128.78-136.15-56.72-42-95.72-79.01-117.77-120.92-.51-.97-1.02-1.95-1.51-2.93 15.66 2.51 32.67 3.85 51.17 3.85 22.75 0 43.39-1.97 62.11-5.57 5.46-1.04 10.75-2.23 15.89-3.54v22.11H708.1v-87c0-19.08-2.14-47.96-10.06-79.55 15.16-45.74 15.72-86.84 15.72-98.45 0-29.67 5.33-80-33.67-153.67-39-73.66-101-123.33-151-162.33S454.42.21 454.42.21c4.65-.14 9.32-.21 14.01-.21 258.7 0 468.43 209.72 468.43 468.43z" />
                                </svg>
                            }
                            label="SubscribeStar"
                            onClick={handleSubscribeStarLogin}
                            color="#ff6b6b"
                        />
                    </div>
                )}

                {/* Divider */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    marginBottom: '24px'
                }}>
                    <div style={{ flex: 1, height: '1px', background: 'rgba(168, 85, 247, 0.2)' }} />
                    <span style={{ fontSize: '13px', color: '#888' }}>or continue with</span>
                    <div style={{ flex: 1, height: '1px', background: 'rgba(168, 85, 247, 0.2)' }} />
                </div>

                {/* Email Form */}
                <form onSubmit={isLogin ? handleEmailLogin : handleRegister}>
                    {!isLogin && (
                        <input
                            type="text"
                            placeholder="Username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '14px 16px',
                                borderRadius: '12px',
                                border: '2px solid rgba(168, 85, 247, 0.3)',
                                background: 'white',
                                fontSize: '15px',
                                marginBottom: '12px',
                                outline: 'none',
                                transition: 'border-color 0.2s'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#a855f7'}
                            onBlur={(e) => e.target.style.borderColor = 'rgba(168, 85, 247, 0.3)'}
                        />
                    )}

                    <input
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '14px 16px',
                            borderRadius: '12px',
                            border: '2px solid rgba(168, 85, 247, 0.3)',
                            background: 'white',
                            fontSize: '15px',
                            marginBottom: '12px',
                            outline: 'none',
                            transition: 'border-color 0.2s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#a855f7'}
                        onBlur={(e) => e.target.style.borderColor = 'rgba(168, 85, 247, 0.3)'}
                    />

                    <input
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '14px 16px',
                            borderRadius: '12px',
                            border: '2px solid rgba(168, 85, 247, 0.3)',
                            background: 'white',
                            fontSize: '15px',
                            marginBottom: '16px',
                            outline: 'none',
                            transition: 'border-color 0.2s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#a855f7'}
                        onBlur={(e) => e.target.style.borderColor = 'rgba(168, 85, 247, 0.3)'}
                    />

                    {error && (
                        <div style={{
                            color: '#ef4444',
                            fontSize: '13px',
                            marginBottom: '12px',
                            textAlign: 'center'
                        }}>
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            width: '100%',
                            padding: '14px',
                            borderRadius: '12px',
                            border: 'none',
                            background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
                            color: 'white',
                            fontSize: '15px',
                            fontWeight: '600',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            opacity: loading ? 0.7 : 1,
                            transition: 'opacity 0.2s'
                        }}
                    >
                        <Mail size={18} />
                        <span>{loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}</span>
                    </button>
                </form>

                {/* Checkboxes for registration */}
                {!isLogin && (
                    <div style={{ marginTop: '16px' }}>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '13px',
                            color: '#666',
                            cursor: 'pointer',
                            marginBottom: '8px'
                        }}>
                            <input
                                type="checkbox"
                                checked={confirmAge}
                                onChange={(e) => setConfirmAge(e.target.checked)}
                                style={{ width: '16px', height: '16px', accentColor: '#a855f7' }}
                            />
                            I confirm that I am 18 years or older.
                        </label>

                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '13px',
                            color: '#666',
                            cursor: 'pointer'
                        }}>
                            <input
                                type="checkbox"
                                checked={agreeTerms}
                                onChange={(e) => setAgreeTerms(e.target.checked)}
                                style={{ width: '16px', height: '16px', accentColor: '#a855f7' }}
                            />
                            I agree to the <a href="/terms" style={{ color: '#a855f7' }}>Terms of Service</a> and <a href="/privacy" style={{ color: '#a855f7' }}>Privacy Policy</a>.
                        </label>
                    </div>
                )}

                {/* Toggle Login/Register */}
                <div style={{
                    marginTop: '20px',
                    textAlign: 'center',
                    fontSize: '14px',
                    color: '#666'
                }}>
                    {isLogin ? "Don't have an account? " : "Already have an account? "}
                    <button
                        type="button"
                        onClick={() => {
                            setIsLogin(!isLogin);
                            setError('');
                        }}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: '#a855f7',
                            fontWeight: '600',
                            cursor: 'pointer',
                            textDecoration: 'underline'
                        }}
                    >
                        {isLogin ? 'Sign Up' : 'Sign In'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LoginModal;


